import { createFileRoute, redirect } from '@tanstack/react-router';

import { type AppInfo } from 'api/types/AppInfo';
import { type ChapterInfo } from 'api/types/ChapterInfo';
import { type ProgressData } from 'api/types/ProgressData';

import { DefaultError } from 'components/DefaultError';

import { AppException, AppExceptionCode } from 'helpers/AppException';

import { getChapterInfoOptions } from 'hooks/data/useChapterInfo';
import { getSoureAppsOptions } from 'hooks/data/useSourceAppData';
import { preloadProgressData } from 'hooks/useQuery/useQueryProgressData';

import { TrainingOverviewView } from 'views/TrainingOverviewView/TrainingOverviewView';

export interface ReturnShape {
  app: AppInfo | undefined;
  apps: AppInfo[];
  chapters: ChapterInfo[];
  allProgress: { progress: ProgressData[]; micro: ProgressData[] };
}

export const Route = createFileRoute('/_auth/_buttonView/_layout/training/_app/$id/')({
  component: TrainingOverviewView,
  beforeLoad: async ({ context, params }) => {
    await preloadProgressData(context.queryClient, [params.id]);
  },

  loader: async ({ context, params }) => {
    const { queryClient } = context;

    try {
      const apps = await queryClient.ensureQueryData(getSoureAppsOptions());

      const app = apps.find(({ id }) => {
        return id === params.id;
      });

      context.title = app?.nameOnDevice ?? null;

      if (!app) {
        throw new AppException('APP_NOT_FOUND');
      }

      const chapters = await queryClient.ensureQueryData(getChapterInfoOptions(params));

      return {
        app,
        apps,
        chapters,
        allProgress: { progress: [], micro: [] },
      } satisfies ReturnShape;
    } catch (error) {
      if (error instanceof AppException) {
        switch (error.code) {
          case AppExceptionCode.APP_NOT_REGISTERED:
            throw redirect({ to: '/info/$id/', params });

          case AppExceptionCode.APP_NOT_FOUND:
            throw redirect({ to: '/404/' });

          default:
            break;
        }
      }
    }

    return {
      app: undefined,
      apps: [],
      chapters: [],
      allProgress: { progress: [], micro: [] },
    } satisfies ReturnShape;
  },

  errorComponent: DefaultError,
});
